import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import MapFirestoreService from '../services/MapFirestoreService';
import TimelineFirestoreService from '../../timeline/services/TimelineFirestoreService';
import { VALID_CATEGORIES } from '../../timeline/constants';
import MarkerEditor from './MarkerEditor';
import MarkerDetailPanel from './MarkerDetailPanel';
import '../styles/MapPage.css';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's default marker icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const service = new MapFirestoreService();
const timelineService = new TimelineFirestoreService();

// Default center: ancient Near East / Israel
const DEFAULT_CENTER = [31.5, 35.5];
const DEFAULT_ZOOM = 7;

/**
 * Component that handles map click events to add markers.
 */
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
}

/**
 * Component that flies the map to a given position when it changes.
 */
function FlyToMarker({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 12, { duration: 1.5 });
    }
  }, [position, map]);
  return null;
}

/**
 * Create a colored circle marker icon — small and offset above city labels.
 */
function createMarkerIcon(color = '#4A90D9') {
  return L.divIcon({
    className: 'map-custom-marker',
    html: `<div style="background-color: ${color}; width: 10px; height: 10px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.8); box-shadow: 0 1px 3px rgba(0,0,0,0.5);"></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 12], // anchor below center so dot sits above the label text
  });
}

const LIVE_COLLECTION = 'map_markers';
const TEST_COLLECTION = 'map_markers_test';

function MapPage() {
  const [markers, setMarkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [editingMarker, setEditingMarker] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [clickedLatLng, setClickedLatLng] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [useTestData, setUseTestData] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [flyToPosition, setFlyToPosition] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterTag, setFilterTag] = useState('');
  const [activeFilterTags, setActiveFilterTags] = useState([]);

  // Switch collection when toggling
  useEffect(() => {
    service.setCollection(useTestData ? TEST_COLLECTION : LIVE_COLLECTION);
    setSelectedMarker(null);
  }, [useTestData]);

  const loadMarkers = useCallback(async () => {
    try {
      const result = await service.getAllMarkers();
      setMarkers(result);
    } catch (error) {
      console.error('Failed to load markers:', error);
    }
  }, [useTestData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadMarkers();
    timelineService.queryByRange(-4000, 2100).then(setTimelineEvents).catch(console.error);
  }, [loadMarkers]);

  // Handle ?marker=ID query param — auto-select marker and fly to it
  useEffect(() => {
    const markerId = searchParams.get('marker');
    if (markerId && markers.length > 0) {
      const found = markers.find(m => m.id === markerId);
      if (found) {
        setSelectedMarker(found);
        setFlyToPosition([found.lat, found.lng]);
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, markers, setSearchParams]);

  const handleMapClick = (latlng) => {
    if (addMode) {
      setClickedLatLng(latlng);
      setEditingMarker(null);
      setShowEditor(true);
      setAddMode(false);
    } else {
      setSelectedMarker(null);
    }
  };

  const handleMarkerClick = (marker) => {
    setSelectedMarker(marker);
  };

  const handleSave = async (data) => {
    try {
      if (editingMarker) {
        await service.updateMarker(editingMarker.id, data);
      } else {
        await service.createMarker(data);
      }
      setShowEditor(false);
      setEditingMarker(null);
      setClickedLatLng(null);
      await loadMarkers();
    } catch (error) {
      console.error('Failed to save marker:', error);
    }
  };

  const handleEdit = () => {
    setEditingMarker(selectedMarker);
    setClickedLatLng(null);
    setShowEditor(true);
  };

  const handleDelete = async () => {
    if (!selectedMarker) return;
    try {
      await service.deleteMarker(selectedMarker.id);
      setSelectedMarker(null);
      await loadMarkers();
    } catch (error) {
      console.error('Failed to delete marker:', error);
    }
  };

  const handleCancel = () => {
    setShowEditor(false);
    setEditingMarker(null);
    setClickedLatLng(null);
  };

  // Filter markers by category and tags
  const filteredMarkers = markers.filter(m => {
    if (filterCategory && m.category !== filterCategory) return false;
    if (activeFilterTags.length > 0) {
      if (!m.tags || !activeFilterTags.every(t => m.tags.includes(t))) return false;
    }
    return true;
  });

  const handleAddFilterTag = (e) => {
    if (e.key === 'Enter' && filterTag.trim()) {
      const tag = filterTag.trim();
      if (!activeFilterTags.includes(tag)) {
        setActiveFilterTags([...activeFilterTags, tag]);
      }
      setFilterTag('');
    }
  };

  return (
    <div className="map-page">
      <div className="map-page-header">
        <h1 className="map-page-title">Historical Map</h1>
        <div className="map-header-actions">
          <button
            className={`map-toggle-data-btn ${useTestData ? 'active' : ''}`}
            onClick={() => setUseTestData(!useTestData)}
            aria-pressed={useTestData}
          >
            {useTestData ? '📋 Test Data' : '📋 Live Data'}
          </button>
          <button
            className={`map-add-mode-btn ${addMode ? 'active' : ''}`}
            onClick={() => setAddMode(!addMode)}
          >
            {addMode ? '📍 Click map to place...' : '📍 Add Marker'}
          </button>
        </div>
      </div>

      {/* Filter panel */}
      <div className="map-filter-panel">
        <span className="map-filter-label">Category:</span>
        <button
          className={`map-filter-btn ${filterCategory === null ? 'active' : ''}`}
          onClick={() => setFilterCategory(null)}
        >
          All
        </button>
        {VALID_CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`map-filter-btn ${filterCategory === cat ? 'active' : ''}`}
            onClick={() => setFilterCategory(filterCategory === cat ? null : cat)}
          >
            {cat.replace(/_/g, ' ')}
          </button>
        ))}
        <span className="map-filter-label map-filter-tag-label">Tags:</span>
        <input
          type="text"
          className="map-filter-tag-input"
          placeholder="Add tag..."
          value={filterTag}
          onChange={e => setFilterTag(e.target.value)}
          onKeyDown={handleAddFilterTag}
        />
        {activeFilterTags.map(tag => (
          <span key={tag} className="map-filter-tag-chip">
            {tag}
            <button onClick={() => setActiveFilterTags(activeFilterTags.filter(t => t !== tag))}>×</button>
          </span>
        ))}
      </div>

      <div className="map-container-wrapper">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          className="map-leaflet-container"
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />
          <MapClickHandler onMapClick={handleMapClick} />
          <FlyToMarker position={flyToPosition} />

          {filteredMarkers.map((m) => (
            <Marker
              key={m.id}
              position={[m.lat, m.lng]}
              icon={createMarkerIcon(m.color || '#4A90D9')}
              eventHandlers={{
                click: () => handleMarkerClick(m),
              }}
            >
              <Tooltip direction="bottom" offset={[0, 8]} permanent className="map-marker-tooltip">
                {m.title}
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>

        {selectedMarker && (
          <MarkerDetailPanel
            marker={selectedMarker}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onClose={() => setSelectedMarker(null)}
          />
        )}
      </div>

      {showEditor && (
        <MarkerEditor
          marker={editingMarker}
          latlng={clickedLatLng}
          onSave={handleSave}
          onCancel={handleCancel}
          timelineEvents={timelineEvents}
        />
      )}
    </div>
  );
}

export default MapPage;
